import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGrowthStore } from '../../store/useGrowthStore';
import { useTodoStore } from '../../store/useTodoStore';
import { useAnnotationStore } from '../../store/useAnnotationStore';
import { useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../api/supabase';
import { DailyGoalPopup } from './DailyGoalPopup';
import { BottleList } from './BottleList';
import { GrowthTodoSection } from './GrowthTodoSection';
import { FocusMode } from './FocusMode';
import { type GrowthTodo } from './GrowthTodoCard';
import { useOutboxStore, getOutboxRetryableCount } from '../../store/useOutboxStore';
import { CloudRetryButton } from '../../components/feedback/CloudRetryButton';
import { Header } from '../../components/layout/Header';

function localDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function normalizeDateKey(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const slash = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (slash) {
    const [, y, m, d] = slash;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }

  return '';
}

export const GrowthPage = () => {
  const { t } = useTranslation();
  const goalDate = useGrowthStore((s) => s.goalDate);
  const dailyGoalEvaluatedDate = useGrowthStore((s) => s.dailyGoalEvaluatedDate);
  const markDailyGoalEvaluated = useGrowthStore((s) => s.markDailyGoalEvaluated);
  const popupDisabled = useGrowthStore((s) => s.popupDisabled);
  const dailyGoalEnabled = useAuthStore((s) => s.preferences.dailyGoalEnabled);
  const authLoading = useAuthStore((s) => s.loading);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const remoteGoalDate = useAuthStore((s) => {
    const date = s.user?.user_metadata?.daily_goal_date;
    return typeof date === 'string' ? date : '';
  });
  const [showGoalPopup, setShowGoalPopup] = useState(false);
  const [focusTodo, setFocusTodo] = useState<GrowthTodo | null>(null);
  const [focusQueue, setFocusQueue] = useState<GrowthTodo[] | undefined>(undefined);
  const [highlightTodoId, setHighlightTodoId] = useState<string | null>(null);
  const pendingSuggestionIntent = useAnnotationStore((s) => s.pendingSuggestionIntent);
  const consumePendingSuggestionIntent = useAnnotationStore((s) => s.consumePendingSuggestionIntent);
  const todos = useTodoStore((s) => s.todos);
  const todosHydrated = useTodoStore((s) => s.hasHydrated);
  const addSubTodos = useTodoStore((s) => s.addSubTodos);
  const fetchBottles = useGrowthStore((s) => s.fetchBottles);
  const growthLoading = useGrowthStore((s) => s.isLoading);
  const growthSyncError = useGrowthStore((s) => s.lastSyncError);
  const fetchTodos = useTodoStore((s) => s.fetchTodos);
  const todoLoading = useTodoStore((s) => s.isLoading);
  const todoSyncError = useTodoStore((s) => s.lastSyncError);
  const outboxRetryCount = useOutboxStore((s) => getOutboxRetryableCount(s.entries));
  const retryOutboxNow = useOutboxStore((s) => s.retryNow);

  const isSyncing = growthLoading || todoLoading;
  const hasSyncError = Boolean(growthSyncError || todoSyncError || outboxRetryCount > 0);

  const handleManualSync = () => {
    void Promise.all([fetchBottles(), fetchTodos(), retryOutboxNow(userId ?? undefined)]);
  };

  // 监听 AI 建议待办高亮事件
  useEffect(() => {
    if (!todosHydrated) return;
    if (!pendingSuggestionIntent || pendingSuggestionIntent.type !== 'todo') return;

    if (Date.now() - pendingSuggestionIntent.createdAt > 45_000) {
      consumePendingSuggestionIntent({ type: 'todo', maxAgeMs: 45_000 });
      return;
    }

    const parentTodoExists = pendingSuggestionIntent.todoId
      ? todos.some((todo) => todo.id === pendingSuggestionIntent.todoId)
      : false;
    if (!parentTodoExists) return;

    const pendingIntent = consumePendingSuggestionIntent({ type: 'todo', maxAgeMs: 45_000 });
    if (!pendingIntent?.todoId) return;

    const steps = pendingIntent.decomposeSteps ?? [];
    if (steps.length > 0) {
      const existingSubTodos = todos.filter((todo) => todo.parentId === pendingIntent.todoId);
      if (existingSubTodos.length === 0) {
        addSubTodos(pendingIntent.todoId, steps);
      }
    }
    setHighlightTodoId(pendingIntent.todoId);
    setTimeout(() => setHighlightTodoId(null), 3000);
  }, [addSubTodos, consumePendingSuggestionIntent, pendingSuggestionIntent, todos, todosHydrated]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ todoId: string }>).detail;
      if (detail?.todoId) {
        setHighlightTodoId(detail.todoId);
        // 3秒后清除高亮
        setTimeout(() => setHighlightTodoId(null), 3000);
      }
    };
    window.addEventListener('suggestion-highlight-todo', handler);
    return () => window.removeEventListener('suggestion-highlight-todo', handler);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) return;
    let cancelled = false;

    const checkPopup = async () => {
      const today = localDateStr();
      if (dailyGoalEvaluatedDate === today) return;

      let freshestRemoteGoalDate = normalizeDateKey(remoteGoalDate);
      try {
        const { data } = await supabase.auth.getUser();
        const latestMetaDate = normalizeDateKey(data.user?.user_metadata?.daily_goal_date);
        if (latestMetaDate) freshestRemoteGoalDate = latestMetaDate;
      } catch {
        // fall back to currently cached metadata
      }

      const hasSubmittedToday =
        normalizeDateKey(goalDate) === today || freshestRemoteGoalDate === today;

      if (!cancelled && dailyGoalEnabled && !popupDisabled && !hasSubmittedToday) {
        setShowGoalPopup(true);
      }

      if (!cancelled) {
        markDailyGoalEvaluated(today);
      }
    };

    void checkPopup();
    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    dailyGoalEnabled,
    dailyGoalEvaluatedDate,
    goalDate,
    markDailyGoalEvaluated,
    popupDisabled,
    remoteGoalDate,
    userId,
  ]);

  return (
    <div className="flex h-full items-center justify-center bg-transparent px-0 md:px-8">
      <div className="app-mobile-page-frame app-scroll-container relative h-full w-full max-w-[430px] text-slate-900 [box-shadow:0_0_0_1px_rgba(0,0,0,0.06),0_24px_64px_rgba(0,0,0,0.1)] md:h-[calc(100%-24px)] md:max-w-[980px] md:rounded-3xl md:border md:border-white/70 md:bg-[#fcfaf7]/85 md:[box-shadow:0_0_0_1px_rgba(255,255,255,0.45),0_24px_64px_rgba(15,23,42,0.12)]">
        <Header className="sticky top-0 z-10 flex items-center justify-between px-4 pb-3 pt-11">
          <h1 className="app-page-title" style={{ color: '#1e293b' }}>{t('growth_title')}</h1>
          {hasSyncError ? (
            <CloudRetryButton
              onClick={handleManualSync}
              disabled={isSyncing}
              title={growthSyncError || todoSyncError || ''}
            />
          ) : null}
        </Header>

        <div className="flex-1 pb-28 pt-2">
          <BottleList />
          <div className="mx-4 my-2 border-t border-slate-200/70" />
          <GrowthTodoSection
            onFocus={(todo) => { setFocusQueue(undefined); setFocusTodo(todo); }}
            onSequentialFocus={(subTodos) => { setFocusTodo(subTodos[0]); setFocusQueue(subTodos); }}
            highlightTodoId={highlightTodoId}
          />
        </div>
      </div>

      {/* Daily goal popup */}
      {showGoalPopup && (
        <DailyGoalPopup onClose={() => setShowGoalPopup(false)} />
      )}

      {/* Focus mode overlay */}
      {focusTodo && (
        <FocusMode
          todo={focusTodo}
          queueTodos={focusQueue}
          onClose={() => { setFocusTodo(null); setFocusQueue(undefined); }}
        />
      )}
    </div>
  );
};
